import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import sample from 'lodash/sample';
import sampleSize from 'lodash/sampleSize';
import values from 'lodash/values';
import Color from 'color';
import {
  EMPTY_OBJECT,
  FULL_STYLE,
  MARKETING_NAV_LINKS,
  RESPONSIVE_SIZE,
} from '../../constants';
import { imageColors, themeColors } from '../../lib/Color';
import { compose } from '../../lib/react';
import { getLatestScenes } from '../../actions/search';
import { setRuntimeVariable } from '../../actions/runtime';
import { showSignup } from '../../actions/user';
import { withFooterDrawer } from '../../components/Footer';
import Aspect from '../../components/Aspect';
import Button from '../../components/Button';
import Header from '../../components/Header';
import Section from '../../components/Section';
import { Brandmark, Details, Refresh, Grid } from '../../components/Icons';
import Link from '../../components/Link';
import HeadlineAnimator from '../../components/HeadlineAnimator';
import HowItWorksGrid from '../../components/HowItWorksGrid';
import SceneCard from '../../components/SceneCard';
import PricingStrip from '../../components/PricingStrip';
import Video from '../../components/Video';
import Style from '../../components/Style';
import {
  L1,
  L2,
  L3,
  L4,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P1,
  P2,
  T1,
} from '../../components/Typography';

import gradedUngradedVideo1920 from '../../assets/graded-ungraded-1920.mp4';
import gradedUngradedVideo1280 from '../../assets/graded-ungraded-1280.mp4';
import gradedUngradedVideo640 from '../../assets/graded-ungraded-640.mp4';
import gradedUngradedPoster from '../../assets/graded-ungraded-poster.jpg';

class HowItWorks extends React.PureComponent {
  static propTypes = {
    responsive: PropTypes.number,
    allClips: PropTypes.arrayOf(PropTypes.object),
    layoutLoaded: PropTypes.bool,
    showSignup: PropTypes.func,
    getLatestScenes: PropTypes.func,
    setMarketingVanityAnimation: PropTypes.func,
  };
  static defaultProps = {
    allClips: EMPTY_OBJECT,
  };

  constructor(props) {
    super(props);

    this.state = {
      tvScene: undefined,
      cardScene: undefined,
    };
  }

  componentWillMount() {
    this.props.getLatestScenes().then(scenes => {
      let choices = sampleSize(scenes, 2);
      this.setState({ tvScene: sample(choices[0]), cardScene: choices[1] });
    });
  }

  render() {
    const { responsive, allClips, showSignup, ...otherProps } = this.props;

    let forTheTapeBottomMargin = 1.6;
    let gridPadding = '';
    let gradedUngradedWidth = '177.7777778%';
    let tvWidth = '95%';
    let gradedUngradedVideo = gradedUngradedVideo640;
    if (responsive >= RESPONSIVE_SIZE.PHONE) {
      gradedUngradedWidth = '100%';
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_PORTRAIT) {
      gridPadding = '5rem';
      tvWidth = '85%';
      gradedUngradedVideo = gradedUngradedVideo1280;
    }
    if (responsive >= RESPONSIVE_SIZE.TABLET_LANDSCAPE) {
      tvWidth = '75%';
      gradedUngradedVideo = gradedUngradedVideo1920;
    }
    if (responsive >= RESPONSIVE_SIZE.DESKTOP) {
      forTheTapeBottomMargin = 2.5;
      gridPadding = '10rem';
    }

    return (
      <div>
        <Section backgroundColor={'#F1F1CD'} bottomAngle={14}>
          <div
            style={{
              maxWidth: '75rem',
              margin: 'auto',
              padding: 'calc(14rem + 8vw) 2rem 2rem',
              textAlign: 'center',
              transform: 'translate3d(0,0,0)',
              transformStyle: 'preserve-3d',
              position: 'relative',
            }}
          >
            <HeadlineAnimator
              show={this.props.layoutLoaded}
              onDone={this.props.setMarketingVanityAnimation}
              topContent={
                <L1
                  style={{
                    display: 'block',
                    marginBottom: `${forTheTapeBottomMargin}rem`,
                    color: '#5E5E4E',
                    opacity: 0.65,
                  }}
                >
                  WE DO IT FOR THE TAPE
                </L1>
              }
              middleContent={
                <H2>
                  Cinematic 4K stock footage. Unlimited access. One
                  low&nbsp;price.<br />
                  <br />Seriously.
                </H2>
              }
            />
          </div>
          <div style={{ margin: '7vw 0 calc(5rem + -7vw)' }}>
            <HowItWorksGrid />
          </div>
        </Section>

        <Section topAngle={14} backgroundColor="#E3E6E8">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <H3
              style={{
                padding: '5rem 0 0',
                margin: 'auto',
                maxWidth: '9em',
                textAlign: 'center',
                color: Style.Colors.shadow2,
              }}
            >
              Be the director. Craft&nbsp;your own story.
            </H3>
            <P1
              style={{
                margin: '1.5rem auto 3.5rem',
                maxWidth: '20em',
                textAlign: 'center',
              }}
            >
              No more sorting through piles of unusable stock footage. Choose
              the right angle from intelligently grouped scenes to tell the
              perfect story.
            </P1>
            <Link to="/browse" style={{ margin: 'auto' }}>
              <Button arrow={true}>Start Browsing</Button>
            </Link>
          </div>

          <div style={{ padding: '6vw 2rem 10vw' }}>
            <div
              style={{
                maxWidth: '64rem',
                margin: 'auto',
                boxShadow: `0 0.2rem 7.6rem 0 ${Color(
                  Style.Colors.shadow2,
                ).alpha(0.5)}`,
                borderRadius: '1rem',
              }}
            >
              <Link
                to={
                  this.state.cardScene
                    ? `/browse/scene/${get(
                        this.state.cardScene,
                        '[0].scene_slug',
                      )}`
                    : undefined
                }
              >
                <SceneCard
                  title={get(this.state.cardScene, '[0].scene_name', '')}
                  typeName={'Scene'}
                  backgroundColor={
                    imageColors(
                      get(
                        this.state.cardScene,
                        '[0].scene_colors',
                        EMPTY_OBJECT,
                      ),
                    ).dark
                  }
                  lqip={get(this.state.cardScene, '[0].scene_lqip')}
                  images={[get(this.state.cardScene, '[0].scene_thumbnail')]}
                  highlightColor={
                    themeColors(
                      get(
                        this.state.cardScene,
                        '[0].scene_colors',
                        EMPTY_OBJECT,
                      ),
                    ).highlight
                  }
                  allClips={
                    this.state.cardScene ? this.state.cardScene : [EMPTY_OBJECT]
                  }
                />
              </Link>
            </div>
          </div>
        </Section>

        <Section
          backgroundColor={Style.Colors.black}
          style={{ position: 'relative', lineHeight: 0 }}
          contentStyle={{ overflow: 'hidden' }}
        >
          <Video
            src={gradedUngradedVideo}
            poster={gradedUngradedPoster}
            style={{
              minWidth: '100%',
              width: gradedUngradedWidth,
              position: 'relative',
              left: `calc((${gradedUngradedWidth} - 100%) / -2)`,
            }}
          />
          <div
            style={{
              ...FULL_STYLE,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
              textAlign: 'right',
              color: Style.Colors.white,
              padding: '5rem',
            }}
          >
            <H2>
              Incredible footage.<br />1080p + 4K ProRes
            </H2>
            <H4>Graded &amp; Ungraded</H4>
          </div>
          <div
            style={{
              ...FULL_STYLE,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              color: Color(Style.Colors.white)
                .alpha(0.8)
                .string(),
            }}
          >
            <L2
              style={{
                margin: '3vw',
                width: '45%',
                flexBasis: '45%',
                textAlign: 'right',
              }}
            >
              GRADED
            </L2>
            <div
              style={{ height: '100%', width: 0, border: '1px solid white' }}
            />
            <L2 style={{ margin: '3vw', width: '45%', flexBasis: '45%' }}>
              UNGRADED
            </L2>
          </div>
        </Section>

        <Section backgroundColor={Style.Colors.black} bottomAngle={14}>
          <div
            style={{
              color: Style.Colors.white,
              position: 'relative',
              top: 'calc(2rem + 7vw)',
              left: 'calc(2rem + 8.5vw)',
              paddingRight: 'calc((2rem + 8.5vw) * 2)',
              paddingBottom: '8rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  width: 'calc(6vw + 5rem)',
                  flex: '0 0 calc(6vw + 5rem)',
                }}
              >
                <Brandmark color={Style.Colors.white} />
              </div>
              <div>
                <H4>Graded & Ungraded?</H4>
              </div>
            </div>
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  width: 'calc(6vw + 5rem)',
                  flex: '0 0 calc(6vw + 5rem)',
                }}
              >
                &nbsp;
              </div>
              <div style={{ maxWidth: '57rem' }}>
                <P1>
                  When you download a clip that is “ungraded,” it simply means
                  we provide it without any adjustment. You’re getting the file
                  directly “as-is” out of the camera. No coloring,
                  stabilization, noise reduction, etc. has been added.
                </P1>
              </div>
            </div>
          </div>
        </Section>

        <Section backgroundColor="#E2EFF3" topAngle={14}>
          <H2
            style={{
              padding: '5rem 0 0',
              margin: '2rem auto 5rem',
              maxWidth: '10em',
              textAlign: 'center',
            }}
          >
            Everything you like, Nothing you don’t.
          </H2>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              paddingLeft: gridPadding,
              paddingBottom: '10rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                margin: '0 6rem',
              }}
            >
              <div style={{ margin: '3rem 0 3rem' }}>
                <Details />
                <H3
                  style={{
                    margin: '2rem 0 1.5rem',
                    color: Style.Colors.shadow1,
                  }}
                >
                  Licensing, simplified
                </H3>
                <P1 style={{ margin: '1.5rem 0 2.5rem', maxWidth: '38rem' }}>
                  Get any clip at any resolution with just a couple clicks.
                  Re-license it as many times as you need.
                </P1>
                {[
                  { res: '4K', name: 'ProRes Graded' },
                  {
                    res: '4K',
                    name: 'ProRes Ungraded',
                    overlayColor: Color('#a7a7a766'),
                  },
                  { res: '1080p', name: 'Graded' },
                ].map(entry => (
                  <div
                    key={entry.name}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minWidth: '15.7rem',
                        minHeight: '8.7rem',
                        margin: '0.6rem 0',
                        backgroundColor: Style.Colors.shadow5,
                        backgroundImage: `url(${get(
                          this.state.cardScene,
                          '[0].scene_thumbnail',
                        )})`,
                        backgroundSize: 'cover',
                        border: `0.3rem solid ${Style.Colors.black}`,
                        borderRadius: '0.6rem',
                      }}
                    >
                      <H3
                        style={{
                          position: 'relative',
                          zIndex: 1,
                          color: Style.Colors.white,
                        }}
                      >
                        {entry.res}
                      </H3>
                      {entry.overlayColor && (
                        <div
                          style={{
                            ...FULL_STYLE,
                            zIndex: 0,
                            backgroundColor: entry.overlayColor,
                          }}
                        />
                      )}
                    </div>
                    <H6 style={{ marginLeft: '2rem' }}>{entry.name}</H6>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                margin: '0 6rem',
              }}
            >
              <div style={{ margin: '3rem 0 3rem' }}>
                <Refresh />
                <H3
                  style={{
                    margin: '2rem 0 1.5rem',
                    color: Style.Colors.shadow1,
                  }}
                >
                  Access anytime, anywhere
                </H3>
                <P1 style={{ margin: '1.5rem 0 2.5rem', maxWidth: '38rem' }}>
                  Browse and license wherever you are. Re-download any clip any
                  time you need it.
                </P1>
                <Button onClick={this.props.showSignup} size="large">
                  Get Started
                </Button>
              </div>

              <div style={{ margin: '3rem 0 3rem' }}>
                <Grid />
                <H3
                  style={{
                    margin: '2rem 0 1.5rem',
                    color: Style.Colors.shadow1,
                  }}
                >
                  Professionally curated
                </H3>
                <P1 style={{ margin: '1.5rem 0 2.5rem', maxWidth: '38rem' }}>
                  From licensed drone operators to experienced videographers, our team of professionals 
                  delivers the highest level of quality in every scene.
                </P1>
              </div>
            </div>
          </div>
        </Section>

        <Section backgroundColor={Style.Colors.white} bottomAngle={14}>
          <H1
            style={{
              padding: '13rem 2rem 0',
              margin: '0 auto',
              maxWidth: '9em',
              textAlign: 'center',
              color: Style.Colors.black,
            }}
          >
            A never-ending supply of footage.
          </H1>
        </Section>

        <Section>
          <div
            style={{
              maxWidth: tvWidth,
              width: '100%',
              margin: '6rem auto 7rem',
            }}
          >
            <Aspect ratio={9 / 16}>
              <div
                style={{
                  ...FULL_STYLE,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: Style.Colors.black,
                  backgroundImage: `url(${get(
                    this.state.tvScene,
                    'scene_poster',
                  )})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: `0.4vw solid ${Style.Colors.black}`,
                  boxShadow: `0 1.6rem 5.6rem 0 ${Color(
                    Style.Colors.shadow2,
                  ).alpha(0.5)}`,
                }}
              >
                <H3
                  style={{
                    margin: 'auto',
                    maxWidth: '9em',
                    textAlign: 'center',
                    color: Style.Colors.white,
                  }}
                >
                  500+ cinematic clips added every month.
                </H3>
              </div>
            </Aspect>
            <div
              style={{
                position: 'relative',
                margin: 'auto',
                width: '22%',
                height: '0.4vw',
                backgroundColor: Style.Colors.black,
              }}
            >
              &nbsp;
            </div>
          </div>
        </Section>

        {this.state.tvScene && (
          <Section
            backgroundColor={themeColors(this.state.tvScene.scene_colors).light}
            topAngle={14}
          >
            <div
              style={{
                padding: '0 0 17rem',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Link to="/browse">
                <Button
                  arrow={true}
                  baseColor={themeColors(this.state.tvScene.scene_colors).light}
                >
                  Browse new scenes
                </Button>
              </Link>
            </div>
          </Section>
        )}

        <Section>
          <PricingStrip />
        </Section>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    responsive: state.runtime.responsive,
    allClips: values(state.clips.clips),
    layoutLoaded: state.runtime.layoutLoaded,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    showSignup: () => dispatch(showSignup()),
    getLatestScenes: () => dispatch(getLatestScenes()),
    setMarketingVanityAnimation: () =>
      dispatch(setRuntimeVariable('marketingVanityAnimation', true)),
  };
}

export default compose(
  withFooterDrawer(),
  connect(mapStateToProps, mapDispatchToProps),
)(HowItWorks);



// WEBPACK FOOTER //
// ./src/routes/howItWorks/HowItWorks.js